'use strict';

var defaults = require('../defaults/Defaults');
var FirebaseObject = require('./FirebaseObject');
var KayleeBase = require('./KayleeBase');
var KayleeCache = require('./KayleeCache');
var KayleeEvent = require('./KayleeEvent');
var KayleeEvents = require('./KayleeEvents');
var KayleeRoom = require('./KayleeRoom');
var KayleeRooms = require('./KayleeRooms');
var KayleeUsers = require('./KayleeUsers');
var KayleeUtil = require('./KayleeUtil');
var log = require('../log/Log').channel('KayleeSite');
var sutl = require('../sutl/Sutl');

var KayleeSite = {
    addUpdatesToCreateSite: addUpdatesToCreateSite,
    addUpdatesToRemoveSite: addUpdatesToRemoveSite,
    fromJson: fromJson,
    getSite: getSite,
};

function addUpdatesToCreateSite(update, companyId, siteId, name, address) {
    update['sites/' + siteId] = {
        address: address,
        companyId: companyId,
        name: name,
    };

    update['index/sitesByCompany/' + companyId + '/' + siteId] = true;

    return KayleeUtil.loadUsersByCompanyIndex({companyId: companyId})
    .then(function(obj) {
        // Add all users with explicit roles for the parent company to the
        // usersBySite index for this site.

        var userPromises = [];
        var addUserRole = function(obj) {
            if (sutl.nodeExists(obj.user,
                ['roles', 'companies', companyId, 'role'])) {
                update['index/usersBySite/' + siteId + '/' + obj.userId] = true;
            }
        };

        for (var userId in obj.usersByCompany) {
            if (!obj.usersByCompany.hasOwnProperty(userId)) continue;
            userPromises.push(
                KayleeUtil.loadUserData({userId: userId})
                .then(addUserRole));
        }

        return Promise.all(userPromises);
    }).then(function() {
        return KayleeEvent.addUpdatesForSiteCreatedEvent(update, companyId, siteId, name);
    });
}

function addUpdatesToRemoveSite(update, companyId, siteId, parentEventId) {
    update['sites/' + siteId] = null;
    update['index/sitesByCompany/' + companyId + '/' + siteId] = null;
    update['index/usersBySite/' + siteId] = null;

    return KayleeEvent.addUpdatesForSiteDeletedEvent(update, siteId, parentEventId)
    .then(function() {
        if (typeof parentEventId === 'undefined' || parentEventId === null) {
            parentEventId = KayleeEvent.getEventIdFromUpdates(update);

            if (parentEventId === null) {
                return Promise.reject('couldn\'t get event id');
            }
        }

        return KayleeUtil.loadUsersBySiteIndex({siteId: siteId}, false);
    })
    .then(function(obj) {
        // Remove site roles for any users who have them.

        var userRolePromises = [];
        var removeUserRole = function(obj) {
            if (sutl.nodeExists(obj.user,
                ['roles', 'companies', companyId,
                'sites', siteId, 'role'])) {
                update['users/' + obj.userId +
                    '/roles/companies/' + companyId +
                    '/sites/' + siteId + '/role'] = null;
                return KayleeEvent.addUpdatesForRoleDeletedEvent(update,
                    obj.userId,
                    obj.user.roles.companies[companyId].sites[siteId].role,
                    companyId, siteId, null,
                    KayleeBase.app.auth().currentUser ? KayleeBase.app.auth().currentUser.uid : null,
                    parentEventId);
            }

            return update;
        };

        for (var userId in obj.usersBySite) {
            if (!obj.usersBySite.hasOwnProperty(userId)) continue;

            userRolePromises.push(
                KayleeUtil.loadUserData({userId: userId})
                .then(removeUserRole));
        }

        return Promise.all(userRolePromises);
    }).then(function() {
        // Remove any rooms belonging to this site. The children equipment and
        // controllers will be handled as well.

        return KayleeUtil.loadRoomsBySiteIndex({siteId: siteId}, false)
        .then(function(obj) {
            var roomPromises = [];

            for (var roomId in obj.roomsBySite) {
                if (!obj.roomsBySite.hasOwnProperty(roomId)) continue;
                roomPromises.push(KayleeRoom.addUpdatesToRemoveRoom(
                    update, companyId, siteId, roomId, parentEventId));
            }

            return Promise.all(roomPromises);
        });
    }).then(function() {
        return KayleeUtil.loadSiteData({ siteId: siteId })
        .then(function(obj) {
            var storageDeletePromises = [];

            ['backgroundImage', 'logo'].forEach(function(prop) {
                var key = sutl.getNode(obj, ['site', 'visualTheme', prop]);
                if (key === null) return;
                var path = ['sites', siteId, 'visualTheme', prop];
                var storageRef = KayleeBase.app.storage().ref(path.join('/'));
                storageDeletePromises.push(storageRef.delete());
            });

            return Promise.all(storageDeletePromises);
        });
    }).then(function() {
        return update;
    });
}

function fromJson(data) {
    var reqProps = [ '$id' ];
    return FirebaseObject.$fromJson(data, 'Site', reqProps, reqProps,
        function(data) { return getSite(data.$id); }
    );
}

function getSite(siteId) {
    var key = 'Site: ' + siteId;
    var site = KayleeCache.get(key);

    if (site === null) {
        site = new Site(siteId);
        KayleeCache.put(key, site);
    }

    return site;
}

function Site(siteId) {
    this.$nodeType = 'site';
    this.$rooms = null;
    FirebaseObject.call(this,
        KayleeBase.app.database().ref('sites/' + siteId));
}

Site.prototype = Object.create(FirebaseObject.prototype);
Site.prototype.constructor = Site;

Site.prototype.$defaults = {
    featureFlags: {
        beta: false,
    },
    settings: {
        appAutoUpdateTime: defaults.inara.appAutoUpdateTime,
        idleEnabled: defaults.inara.idleEnabled,
        idleScreen: defaults.inara.idleScreen,
        idleTimeout: defaults.inara.idleTimeout,
        inaraChannel: defaults.inara.channel,
        micsMuteBehaviorOnRoomPowerOff: defaults.inara.micsMuteBehaviorOnRoomPowerOff,
        recentCallsEnabled: defaults.inara.recentCallsEnabled,
        remoteControlEnabled: defaults.inara.remoteControlEnabled,
        remoteControlSessionDurationMinutes: defaults.inara.remoteControlSessionDurationMinutes,
        roomPowerOffEnabled: defaults.inara.roomPowerOffEnabled,
        sleepEnabled: defaults.inara.sleepEnabled,
        sleepTimeout: defaults.inara.sleepTimeout,
        telephonyDialingPrefixDelay: defaults.inara.telephonyDialingPrefixDelay,
        timeFormat: defaults.inara.timeFormat,
        timeZone: defaults.inara.timeZone,
    },
};

Site.prototype.$objProps = {
    settings: {
        roomPowerOffSchedule: true,
    },
};

Site.prototype.$storage = {
    visualTheme: {
        backgroundImage: true,
        logo: true,
    },
};

Site.prototype.$includeInChangedEvent = {
    name: true,
    address: true,
    settings: true,
    // TODO: add visualTheme
};

Site.prototype.$addUpdatesForChangedEvent =
    KayleeEvent.addUpdatesForSiteChangedEvent;

Site.prototype.$toJson = function() {
    return FirebaseObject.prototype.$toJson.call(this, [ '$id' ]);
};

Site.prototype.toString = function() {
    if (this.$internalIsLoaded) {
        return '[Site "' + this.name + '" (' + this.$id + ')]';
    }

    return '[Site (' + this.$id + ')]';
};

Site.prototype.$remove = function() {
    if (!this.$internalIsLoaded) {
        var err = new Error(this + ' not loaded');
        log.error(err.message);
        return Promise.reject(err);
    }

    var self = this;
    var update = {};

    return addUpdatesToRemoveSite(update, self.companyId, self.$id)
    .then(function(update) {
        return KayleeBase.update(update);
    })
    .then(function() {
        log.info('removed ' + self);
    });
};

Site.prototype.$getRooms = function(userId) {
    return KayleeRooms.getRooms(this, userId);
};

Site.prototype.$addRoom = function(name) {
    if (!this.$internalIsLoaded) {
        var err = new Error(this + ' not loaded');
        log.error(err.message);
        return Promise.reject(err);
    }

    var self = this;
    var roomId = self.$ref.push().key;

    return KayleeRoom.addUpdatesToCreateRoom({}, self.companyId, self.$id,
        roomId, name)
    .then(function(update) {
        return KayleeBase.update(update);
    }).then(function() {
        log.info('added [Room "' + name + '" (' + roomId + ')]');
        return roomId;
    });
};

Site.prototype.$getUsers = function() {
    return KayleeUsers.getUsers(this);
};

Site.prototype.$getEvents = function(limit) {
    return KayleeEvents.getEvents(this, limit);
};

Site.prototype.$getAlertCount = function() {
    if (!this.$internalIsLoaded) return 0;

    if (this.$rooms === null) {
        this.$rooms = this.$getRooms();
    }

    return this.$rooms.$getAlertCount();
};

Site.prototype.$getControllerCount = function() {
    if (!this.$internalIsLoaded) return 0;

    if (this.$rooms === null) {
        this.$rooms = this.$getRooms();
    }

    return this.$rooms.$getControllerCount();
};

Site.prototype.$getInCallCount = function() {
    if (!this.$internalIsLoaded) return 0;

    if (this.$rooms === null) {
        this.$rooms = this.$getRooms();
    }

    return this.$rooms.$getInCallCount();
};

Site.prototype.$getRoomCount = function() {
    if (!this.$internalIsLoaded) return 0;

    if (this.$rooms === null) {
        this.$rooms = this.$getRooms();
    }

    return this.$rooms.length;
};

module.exports = KayleeSite;
